import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { BRAND, LOGOS } from "@/brand";
import Crown from "@/components/Crown";

export default function Hero({ ready }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 60, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 60, damping: 18 });

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      data-testid="hero-section-kinetic"
      onMouseMove={onMouseMove}
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden concrete-grain"
      style={{ perspective: 1200 }}
    >
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src="/brand/campaign-08.webp"
          alt=""
          className="h-[120%] w-full object-cover object-top opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0B]/70 via-[#0A0A0B]/55 to-[#0A0A0B]" />
      </motion.div>

      <Crown className="crown-float absolute top-[14%] right-[8%] h-24 w-36 text-zinc-800/70 hidden sm:block" />
      <Crown className="absolute bottom-[18%] left-[5%] h-16 w-24 text-zinc-800/50 rotate-12 hidden sm:block" />

      <span className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 -rotate-90 text-[10px] tracking-[0.5em] text-zinc-600 uppercase whitespace-nowrap hidden md:block">
        Est. Los Angeles — Worldwide
      </span>
      <span className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 rotate-90 text-[10px] tracking-[0.5em] text-zinc-600 uppercase whitespace-nowrap hidden md:block">
        No Comfort — No Mercy
      </span>

      <div className="relative z-10 flex flex-col items-center px-4">
        <h1 className="sr-only">{BRAND.name}</h1>
        <motion.div style={{ rotateX, rotateY, opacity: fade }} className="logo-blend">
          <div className="overflow-hidden" data-testid="hero-title-stacked">
            <motion.img
              src={LOGOS.wordmark}
              alt={BRAND.name}
              initial={{ y: "45%", opacity: 0, scale: 1.1, filter: "blur(12px)" }}
              animate={ready ? { y: 0, opacity: 1, scale: 1, filter: "blur(0px)" } : {}}
              transition={{ delay: 0.25, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="logo-fade w-[88vw] max-w-3xl"
            />
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={ready ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-6 max-w-md text-center text-xs sm:text-sm leading-relaxed text-zinc-400 tracking-wider"
        >
          Premium Southern California streetwear. Forged in concrete, crowned in chrome.
        </motion.p>

        <motion.button
          data-testid="hero-cta-enter-vault"
          initial={{ opacity: 0, y: 16 }}
          animate={ready ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.1, duration: 0.8 }}
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => document.querySelector("#shop")?.scrollIntoView({ behavior: "smooth" })}
          className="group relative mt-10 border border-zinc-500/70 bg-zinc-100 px-10 py-4 text-xs font-bold tracking-[0.35em] text-zinc-950 uppercase transition-colors duration-300 hover:bg-transparent hover:text-zinc-100"
        >
          Enter the Vault
        </motion.button>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={ready ? { opacity: 1 } : {}}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="text-[9px] tracking-[0.4em] text-zinc-600 uppercase">Scroll</span>
        <div className="h-10 w-px bg-gradient-to-b from-zinc-500 to-transparent" />
      </motion.div>
    </section>
  );
}
